import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Drawer,
  Divider,
  Chip,
  IconButton
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { tokens } from "../../theme";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import Header from "../../components/Header";
import StatBox from "../../components/StatBox";
import { DataGrid } from "@mui/x-data-grid";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";
import StrapiSwitch from "../../components/Switch";
import { format } from "date-fns";

const ManageFraud = () => {
  const { success, snackError, cookies } = useAuth();
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.up("sm"));
  const colors = tokens(theme.palette.mode);
  const axiosPrivate = useAxiosPrivate();

  // Tab State: 0 = Fraud Rules, 1 = Pending Decisions, 2 = Counterparty Blocklist, 3 = Decisions History, 4 = Held Transactions
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);

  // User roles check
  const [isMaker, setIsMaker] = useState(false);
  const [isChecker, setIsChecker] = useState(false);

  // 1. Fraud Rules State
  const [isEvent, setIsEvent] = useState(false);
  const [rulesList, setRulesList] = useState([]);
  const [eventsList, setEventsList] = useState([]);
  const [eventsStats, setEventsStats] = useState({ total: 0, resolved: 0, unresolved: 0 });
  const [newDetails, setNewDetails] = useState({}); // used as refresh trigger

  // 2. Pending Decisions State
  const [pendingActions, setPendingActions] = useState([]);
  const [searchPendingQuery, setSearchPendingQuery] = useState("");

  // 3. Counterparty Blocklist State
  const [blocklist, setBlocklist] = useState([]);
  const [searchBlockQuery, setSearchBlockQuery] = useState("");
  const [openAddBlocklistModal, setOpenAddBlocklistModal] = useState(false);
  const [blockAccountsInput, setBlockAccountsInput] = useState("");
  const [blockReasonInput, setBlockReasonInput] = useState("");

  // 4. Decisions History State
  const [decisions, setDecisions] = useState([]);
  const [decisionsTotalRows, setDecisionsTotalRows] = useState(0);
  const [decisionsPage, setDecisionsPage] = useState(1);
  const [decisionsPageSize, setDecisionsPageSize] = useState(10);
  const [decisionsStats, setDecisionsStats] = useState({
    total: 0,
    approved: 0,
    rejected: 0,
  });

  // 5. Held Transactions State
  const [heldTransactions, setHeldTransactions] = useState([]);
  const [heldTotalRows, setHeldTotalRows] = useState(0);
  const [heldPage, setHeldPage] = useState(1);
  const [heldPageSize, setHeldPageSize] = useState(10);
  const [heldStatusFilter, setHeldStatusFilter] = useState("");
  const [heldUserIdFilter, setHeldUserIdFilter] = useState("");

  // Drawer / Inspection Detail State
  const [inspectType, setInspectType] = useState(null); // 'pending' | 'decision' | 'held'
  const [inspectRecord, setInspectRecord] = useState(null);
  const [inspectDetailLoading, setInspectDetailLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Action Dialog State (Unified Confirmation Dialog for actions needing comment)
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [actionTitle, setActionTitle] = useState("");
  const [actionSubtitle, setActionSubtitle] = useState("");
  const [actionTargetRecord, setActionTargetRecord] = useState(null);
  const [actionComment, setActionComment] = useState("");
  const [actionTypeStr, setActionTypeStr] = useState(""); // e.g. "PENDING_APPROVE", "PENDING_REJECT", "HELD_MAKER_APPROVE", "HELD_MAKER_REJECT", "HELD_CHECKER_APPROVE", "HELD_CHECKER_REJECT"
  const [actionLoading, setActionLoading] = useState(false);

  // Helper date formatter
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "yyyy-MM-dd HH:mm");
    } catch (e) {
      return String(dateString);
    }
  };

  const rolesString = cookies ? JSON.stringify(cookies?.auth?.user?.roles || cookies?.cookieAuth?.user?.roles) : "";

  // Determine User Roles
  useEffect(() => {
    let roles = [];
    if (cookies?.auth?.user?.roles) {
      roles = cookies.cookieAuth?.user?.roles || cookies.auth?.user?.roles || [];
    }
    if (!roles.length) {
      const rawRoles = localStorage.getItem("roles");
      try {
        roles = JSON.parse(rawRoles || "[]");
      } catch {
        roles = rawRoles ? [rawRoles] : [];
      }
    }
    // Handle array of strings or array of objects
    const normalized = roles.map(r => {
      if (typeof r === "object" && r?.authority) return String(r.authority).toUpperCase();
      if (typeof r === "object" && r?.name) return String(r.name).toUpperCase();
      return String(r).toUpperCase();
    });

    setIsMaker(normalized.includes("SUPER_ADMIN") || normalized.includes("ROLE_SUPER_ADMIN"));
    setIsChecker(normalized.includes("OWNER") || normalized.includes("ROLE_OWNER"));
  }, [rolesString]);

  // Helper resolvers for dynamic backend response envelope formats
  const resolveList = (resData) => {
    if (!resData) return [];
    if (resData.data && Array.isArray(resData.data.content)) return resData.data.content;
    if (Array.isArray(resData.content)) return resData.content;
    if (Array.isArray(resData.data)) return resData.data;
    if (Array.isArray(resData)) return resData;
    return [];
  };

  const resolveTotalElements = (resData, listLength) => {
    if (!resData) return 0;
    if (resData.data && typeof resData.data.totalElements === "number") return resData.data.totalElements;
    if (typeof resData.totalElements === "number") return resData.totalElements;
    return listLength;
  };

  // 1. Fetch Fraud Rules
  const fetchFraudRules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/transaction-payment/api/v1/fraud-rule");
      const resData = response?.data;
      if (resData && resData.success === false) {
        snackError(resData.message || "Failed to fetch fraud rules.");
        setRulesList([]);
        return;
      }
      setRulesList(resolveList(resData));
    } catch (error) {
      console.error(error);
      snackError("Failed to fetch fraud rules.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1b. Fetch Fraud Events
  const fetchFraudEvents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/transaction-payment/api/v1/fraud-event");
      const resData = response?.data;
      if (resData && resData.success === false) {
        snackError(resData.message || "Failed to fetch fraud events.");
        setEventsList([]);
        return;
      }
      const list = resolveList(resData);
      const formatted = list.map((item, index) => ({
        ...item,
        id: item.eventId || item.id || `event_${index}`
      }));
      setEventsList(formatted);

      // Calculate stats
      const total = formatted.length;
      const resolved = formatted.filter(e => !e.active).length;
      const unresolved = formatted.filter(e => e.active).length;
      setEventsStats({ total, resolved, unresolved });
    } catch (error) {
      console.error(error);
      snackError("Failed to fetch fraud events.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Fetch Pending Decisions
  const fetchPendingDecisions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/transaction-payment/api/v1/admin/fraud/pending");
      const resData = response?.data;
      if (resData && resData.success === false) {
        snackError(resData.message || "Failed to fetch pending fraud decisions.");
        setPendingActions([]);
        return;
      }
      const list = resolveList(resData);
      const formatted = list.map((item, index) => ({
        ...item,
        id: item.id || item.reference || `pending_${index}`
      }));
      setPendingActions(formatted);
    } catch (error) {
      console.error(error);
      snackError("Failed to fetch pending fraud decisions.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 3. Fetch Counterparty Blocklist
  const fetchBlocklist = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/transaction-payment/api/v1/admin/fraud/blocklist/counterparty");
      const resData = response?.data;
      if (resData && resData.success === false) {
        snackError(resData.message || "Failed to fetch counterparty blocklist.");
        setBlocklist([]);
        return;
      }
      const list = resolveList(resData);
      const formatted = list.map((item, index) => {
        if (typeof item === "string") {
          return {
            id: item,
            account: item,
            reason: "N/A",
            blockedBy: "System / Admin",
            createdAt: null
          };
        }
        return {
          ...item,
          id: item.id || item.account || `block_${index}`
        };
      });
      setBlocklist(formatted);
    } catch (error) {
      console.error(error);
      snackError("Failed to fetch counterparty blocklist.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 4. Fetch Decisions History & Summary Stats
  const fetchDecisionsHistory = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch stats summary first
      try {
        const statsRes = await axiosPrivate.get("/transaction-payment/api/v1/admin/fraud/decisions/summary");
        const statsData = statsRes?.data || statsRes;
        if (statsData && statsData.success !== false) {
          let total = 0;
          let approved = 0;
          let rejected = 0;

          const statsObject = statsData.data || statsData;
          if (statsObject && typeof statsObject === "object") {
            const byAction = statsObject.by_action || statsObject.byAction || {};
            for (const [key, val] of Object.entries(byAction)) {
              const uKey = key.toUpperCase();
              const count = Number(val || 0);
              if (uKey.includes("APPROVE") || uKey.includes("ALLOW") || uKey.includes("PASS")) {
                approved += count;
              } else if (uKey.includes("REJECT") || uKey.includes("DENY") || uKey.includes("BLOCK")) {
                rejected += count;
              }
              total += count;
            }

            if (total === 0) {
              if (Array.isArray(statsObject)) {
                statsObject.forEach(item => {
                  const act = String(item.action || item.decision || "").toUpperCase();
                  const count = Number(item.count || item.total || 0);
                  if (act.includes("APPROVE") || act.includes("ALLOW") || act.includes("PASS")) {
                    approved = count;
                  } else if (act.includes("REJECT") || act.includes("DENY") || act.includes("BLOCK")) {
                    rejected = count;
                  }
                  total += count;
                });
              } else {
                approved = Number(statsObject.approved || statsObject.approvedCount || statsObject.approve || 0);
                rejected = Number(statsObject.rejected || statsObject.rejectedCount || statsObject.reject || 0);
                total = Number(statsObject.total || statsObject.totalCount || (approved + rejected));
              }
            }
          }
          setDecisionsStats({ total, approved, rejected });
        }
      } catch (err) {
        console.error("Error fetching decisions stats summary:", err);
      }

      // Fetch paginated decisions
      const response = await axiosPrivate.get("/transaction-payment/api/v1/admin/fraud/decisions", {
        params: {
          page: decisionsPage - 1,
          size: decisionsPageSize
        }
      });
      const resData = response?.data;
      if (resData && resData.success === false) {
        snackError(resData.message || "Failed to fetch decisions history.");
        setDecisions([]);
        setDecisionsTotalRows(0);
        return;
      }
      const content = resolveList(resData);
      const totalElements = resolveTotalElements(resData, content.length);
      const formatted = content.map((item, index) => ({
        ...item,
        id: item.id || item.reference || `decision_${index}`
      }));
      setDecisions(formatted);
      setDecisionsTotalRows(totalElements);
    } catch (error) {
      console.error(error);
      snackError("Failed to fetch decisions history.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisionsPage, decisionsPageSize]);

  // 5. Fetch Held Transactions
  const fetchHeldTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: heldPage - 1,
        size: heldPageSize
      };
      if (heldStatusFilter) params.status = heldStatusFilter;
      if (heldUserIdFilter) params.userId = heldUserIdFilter;

      const response = await axiosPrivate.get("/transaction-payment/api/v1/admin/held-transactions", { params });
      const resData = response?.data;
      if (resData && resData.success === false) {
        snackError(resData.message || "Failed to fetch held transactions.");
        setHeldTransactions([]);
        setHeldTotalRows(0);
        return;
      }
      const content = resolveList(resData);
      const totalElements = resolveTotalElements(resData, content.length);
      const formatted = content.map((item, index) => ({
        ...item,
        id: item.id || item.reference || `held_${index}`
      }));
      setHeldTransactions(formatted);
      setHeldTotalRows(totalElements);
    } catch (error) {
      console.error(error);
      snackError("Failed to fetch held transactions.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heldPage, heldPageSize, heldStatusFilter, heldUserIdFilter]);

  // Effect to load data based on active tab
  useEffect(() => {
    if (activeTab === 0) {
      if (isEvent) fetchFraudEvents();
      else fetchFraudRules();
    }
    else if (activeTab === 1) fetchPendingDecisions();
    else if (activeTab === 2) fetchBlocklist();
    else if (activeTab === 3) fetchDecisionsHistory();
    else if (activeTab === 4) fetchHeldTransactions();
  }, [activeTab, isEvent, fetchFraudRules, fetchFraudEvents, fetchPendingDecisions, fetchBlocklist, fetchDecisionsHistory, fetchHeldTransactions, newDetails]);

  // Handle Switch Toggle for Fraud Rules
  const handleToggleRule = async (row) => {
    try {
      const response = await axiosPrivate.put("/transaction-payment/api/v1/fraud-rule/edit", {
        ...row,
        activated: !row.activated
      });
      success("Fraud rule updated successfully!");
      setNewDetails(response?.data?.data || {});
    } catch (error) {
      console.error(error);
      snackError("Failed to update fraud rule.");
    }
  };

  // Handle Undo Event Action for Fraud Events
  const handleUndoEventAction = async (row) => {
    try {
      const response = await axiosPrivate.post("/transaction-payment/api/v1/fraud-event/undo-action", {
        eventRuleName: row?.eventRuleName,
        userId: row?.userId
      });
      if (response?.data?.success || response?.data?.status === true || !response?.data?.error) {
        success(response?.data?.message || "Event action undone successfully!");
        fetchFraudEvents();
      } else {
        snackError(response?.data?.message || "Failed to undo event action.");
      }
    } catch (error) {
      console.error(error);
      snackError(error?.response?.data?.message || "Failed to undo event action.");
    }
  };

  // Inspect details inside Drawer
  const handleInspect = async (type, record) => {
    setInspectType(type);
    setInspectRecord(record);
    setInspectDetailLoading(true);
    setDrawerOpen(true);

    try {
      if (type === "pending" && record.reference) {
        const res = await axiosPrivate.get(`/transaction-payment/api/v1/admin/fraud/pending/${record.reference}`);
        if (res?.data) setInspectRecord(res.data);
      } else if (type === "decision" && record.id) {
        const res = await axiosPrivate.get(`/transaction-payment/api/v1/admin/fraud/decisions/${record.id}`);
        if (res?.data) setInspectRecord(res.data);
      } else if (type === "held" && record.reference) {
        const res = await axiosPrivate.get(`/transaction-payment/api/v1/admin/held-transactions/${record.reference}`);
        if (res?.data) setInspectRecord(res.data);
      }
    } catch (error) {
      console.warn("Failed to fetch full record detail, displaying list representation:", error);
    } finally {
      setInspectDetailLoading(false);
    }
  };

  // Open Action confirmation dialog with comments field
  const handleOpenActionDialog = (type, record, title, subtitle) => {
    setActionTypeStr(type);
    setActionTargetRecord(record);
    setActionTitle(title);
    setActionSubtitle(subtitle);
    setActionComment("");
    setOpenActionDialog(true);
  };

  // Submit Action Confirmation
  const handleActionSubmit = async (e) => {
    e.preventDefault();
    if (!actionComment.trim()) {
      snackError("Comments are required.");
      return;
    }

    setActionLoading(true);
    const ref = actionTargetRecord?.reference;
    try {
      let res;
      if (actionTypeStr === "PENDING_APPROVE") {
        res = await axiosPrivate.post(`/transaction-payment/api/v1/admin/fraud/pending/${ref}/approve?comment=${encodeURIComponent(actionComment)}`);
      } else if (actionTypeStr === "PENDING_REJECT") {
        res = await axiosPrivate.post(`/transaction-payment/api/v1/admin/fraud/pending/${ref}/reject?comment=${encodeURIComponent(actionComment)}`);
      } else if (actionTypeStr === "HELD_MAKER_APPROVE") {
        res = await axiosPrivate.post(`/transaction-payment/api/v1/admin/held-transactions/${ref}/maker/approve?comment=${encodeURIComponent(actionComment)}`);
      } else if (actionTypeStr === "HELD_MAKER_REJECT") {
        res = await axiosPrivate.post(`/transaction-payment/api/v1/admin/held-transactions/${ref}/maker/reject?comment=${encodeURIComponent(actionComment)}`);
      } else if (actionTypeStr === "HELD_CHECKER_APPROVE") {
        res = await axiosPrivate.post(`/transaction-payment/api/v1/admin/held-transactions/${ref}/checker/approve?comment=${encodeURIComponent(actionComment)}`);
      } else if (actionTypeStr === "HELD_CHECKER_REJECT") {
        res = await axiosPrivate.post(`/transaction-payment/api/v1/admin/held-transactions/${ref}/checker/reject?comment=${encodeURIComponent(actionComment)}`);
      }

      if (res?.data?.success || res?.data?.status === true || !res?.data?.error) {
        success(res?.data?.message || "Action processed successfully!");
        setOpenActionDialog(false);
        setDrawerOpen(false);
        // Refresh appropriate view
        if (activeTab === 1) fetchPendingDecisions();
        if (activeTab === 4) fetchHeldTransactions();
      } else {
        snackError(res?.data?.message || "Failed to process the requested action.");
      }
    } catch (err) {
      console.error(err);
      snackError(err?.response?.data?.message || "An error occurred while executing the decision.");
    } finally {
      setActionLoading(false);
    }
  };

  // Add Accounts to Blocklist
  const handleAddBlocklist = async (e) => {
    e.preventDefault();
    const accounts = blockAccountsInput
      .split(",")
      .map(acc => acc.trim())
      .filter(acc => acc.length > 0);

    if (accounts.length === 0) {
      snackError("Please enter at least one valid account number.");
      return;
    }
    if (!blockReasonInput.trim()) {
      snackError("Please enter a reason.");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosPrivate.post("/transaction-payment/api/v1/admin/fraud/blocklist/counterparty", {
        accounts,
        reason: blockReasonInput
      });

      if (response?.data?.success || response?.data?.status === true || !response?.data?.error) {
        success(response?.data?.message || "Accounts successfully added to blocklist.");
        setOpenAddBlocklistModal(false);
        setBlockAccountsInput("");
        setBlockReasonInput("");
        fetchBlocklist();
      } else {
        snackError(response?.data?.message || "Failed to add accounts to blocklist.");
      }
    } catch (error) {
      console.error(error);
      snackError(error?.response?.data?.message || "An error occurred while blocklisting.");
    } finally {
      setLoading(false);
    }
  };

  // Remove account from blocklist confirmation dialog
  const handleRemoveBlocklistConfirm = (accountNum) => {
    handleOpenActionDialog(
      "BLOCKLIST_REMOVE",
      { reference: accountNum, account: accountNum },
      "Remove From Blocklist",
      `Are you sure you want to remove account number ${accountNum} from the blocklist?`
    );
  };

  // Remove Account Execution
  const handleRemoveBlocklistSubmit = async () => {
    setActionLoading(true);
    const acc = actionTargetRecord?.account;
    try {
      const res = await axiosPrivate.delete(`/transaction-payment/api/v1/admin/fraud/blocklist/counterparty/${acc}`);
      if (res?.data?.success || res?.data?.status === true || !res?.data?.error) {
        success(res?.data?.message || "Account successfully removed from blocklist.");
        setOpenActionDialog(false);
        fetchBlocklist();
      } else {
        snackError(res?.data?.message || "Failed to remove account.");
      }
    } catch (err) {
      console.error(err);
      snackError(err?.response?.data?.message || "An error occurred.");
    } finally {
      setActionLoading(false);
    }
  };

  // Dynamic search filtering
  const getFilteredPendingActions = () => {
    const query = searchPendingQuery.trim().toLowerCase();
    if (!query) return pendingActions;
    return pendingActions.filter(item =>
      (item.reference && item.reference.toLowerCase().includes(query)) ||
      (item.account && item.account.toLowerCase().includes(query)) ||
      (item.makerEmail && item.makerEmail.toLowerCase().includes(query))
    );
  };

  const getFilteredBlocklist = () => {
    const query = searchBlockQuery.trim().toLowerCase();
    if (!query) return blocklist;
    return blocklist.filter(item =>
      (item.account && item.account.toLowerCase().includes(query)) ||
      (item.reason && item.reason.toLowerCase().includes(query)) ||
      (item.blockedBy && item.blockedBy.toLowerCase().includes(query))
    );
  };

  // Get Columns mapping dynamically
  const getColumns = () => {
    switch (activeTab) {
      case 0: // Fraud Rules & Events
        if (isEvent) {
          return [
            { field: "name", headerName: "Name", width: 200, renderCell: ({ value }) => <strong>{value || "N/A"}</strong> },
            { field: "emailAddress", headerName: "User Email", width: 220 },
            { field: "phoneNumber", headerName: "Phone Number", width: 180 },
            { field: "eventRuleName", headerName: "Rule Violated", width: 220 },
            {
              field: "active",
              headerName: "Status",
              width: 150,
              renderCell: ({ value }) => (
                <Chip
                  label={value ? "Unresolved" : "Resolved"}
                  color={value ? "error" : "success"}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )
            },
            {
              field: "createdAt",
              headerName: "Date",
              width: 180,
              renderCell: ({ value }) => formatDate(value)
            },
            {
              field: "action",
              headerName: "Action",
              width: 150,
              renderCell: ({ row }) => (
                <StrapiSwitch
                  checked={row.active}
                  disabled={!row.active}
                  onChange={() => handleUndoEventAction(row)}
                />
              )
            }
          ];
        }
        return [
          { field: "ruleName", headerName: "Rule Name", width: 250 },
          { field: "maximumAttempt", headerName: "Number of Attempts", width: 200 },
          {
            field: "activated",
            headerName: "Activate",
            width: 150,
            renderCell: ({ row }) => (
              <StrapiSwitch checked={row.activated} onChange={() => handleToggleRule(row)} />
            )
          }
        ];
      case 1: // Pending Decisions
        return [
          { field: "reference", headerName: "Reference", width: 180, renderCell: ({ value }) => <strong>{value || "N/A"}</strong> },
          {
            field: "actionType",
            headerName: "Action Type",
            width: 120,
            renderCell: ({ value }) => (
              <Chip label={value} color={value === "BLOCK" ? "error" : "success"} size="small" sx={{ fontWeight: 600 }} />
            )
          },
          { field: "account", headerName: "Account / Target", width: 160 },
          { field: "makerEmail", headerName: "Initiated By", width: 200 },
          {
            field: "createdAt",
            headerName: "Date Created",
            width: 180,
            renderCell: ({ value }) => formatDate(value)
          },
          {
            field: "status",
            headerName: "Status",
            width: 120,
            renderCell: () => <Chip label="PENDING" color="warning" size="small" sx={{ fontWeight: 600 }} />
          },
          {
            field: "actions",
            headerName: "Actions",
            width: 280,
            renderCell: ({ row }) => (
              <Box display="flex" gap={1}>
                <Button variant="outlined" color="inherit" size="small" onClick={() => handleInspect("pending", row)}>
                  Inspect
                </Button>
                <Button variant="contained" color="success" size="small" onClick={() => handleOpenActionDialog("PENDING_APPROVE", row, "Approve Fraud Action", `Are you sure you want to approve this action for reference: ${row.reference}?`)}>
                  Approve
                </Button>
                <Button variant="contained" color="error" size="small" onClick={() => handleOpenActionDialog("PENDING_REJECT", row, "Reject Fraud Action", `Are you sure you want to reject this action for reference: ${row.reference}?`)}>
                  Reject
                </Button>
              </Box>
            )
          }
        ];
      case 2: // Counterparty Blocklist
        return [
          { field: "account", headerName: "Account Number", width: 180, renderCell: ({ value }) => <strong>{value}</strong> },
          { field: "reason", headerName: "Block Reason", width: 250 },
          { field: "blockedBy", headerName: "Blocked By", width: 180 },
          {
            field: "createdAt",
            headerName: "Date Blocked",
            width: 180,
            renderCell: ({ value }) => formatDate(value)
          },
          {
            field: "status",
            headerName: "Status",
            width: 120,
            renderCell: () => <Chip label="BLOCKED" color="error" size="small" sx={{ fontWeight: 600 }} />
          },
          {
            field: "actions",
            headerName: "Actions",
            width: 120,
            renderCell: ({ row }) => (
              <Button variant="contained" color="error" size="small" onClick={() => handleRemoveBlocklistConfirm(row.account)}>
                Remove
              </Button>
            )
          }
        ];
      case 3: // Decisions History
        return [
          { field: "id", headerName: "Decision ID", width: 120, renderCell: ({ value }) => <strong>{value}</strong> },
          {
            field: "userId",
            headerName: "User / Account Info",
            width: 250,
            renderCell: ({ row }) => (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.userId || row.checkerEmail || "N/A"}</Typography>
                {row.fromAccount && <Typography variant="caption" color="textSecondary" display="block">From: {row.fromAccount}</Typography>}
                {row.toAccount && <Typography variant="caption" color="textSecondary" display="block">To: {row.toAccount}</Typography>}
              </Box>
            )
          },
          {
            field: "amount",
            headerName: "Transaction Details",
            width: 180,
            renderCell: ({ row }) => (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.amount !== undefined ? `₦${row.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "N/A"}
                </Typography>
                {row.channel && <Typography variant="caption" color="textSecondary">{row.channel}</Typography>}
              </Box>
            )
          },
          { field: "reference", headerName: "Reference", width: 180 },
          {
            field: "action",
            headerName: "Decision",
            width: 140,
            renderCell: ({ row }) => {
              const val = String(row.action || row.actionTaken || "").toUpperCase();
              let chipColor = "default";
              if (val.includes("ALLOW") || val.includes("APPROVE")) chipColor = "success";
              else if (val.includes("DENY") || val.includes("BLOCK") || val.includes("REJECT")) chipColor = "error";

              return (
                <Box>
                  <Chip label={val || "N/A"} color={chipColor} size="small" sx={{ fontWeight: 600 }} />
                  {row.score !== undefined && (
                    <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
                      Score: {row.score}
                    </Typography>
                  )}
                </Box>
              );
            }
          },
          {
            field: "comment",
            headerName: "Reason / Comment",
            width: 200,
            renderCell: ({ row }) => {
              let rulesCount = 0;
              try {
                if (row.rulesFiredJson) {
                  const rules = JSON.parse(row.rulesFiredJson);
                  rulesCount = Array.isArray(rules) ? rules.length : 0;
                }
              } catch (e) {}

              return (
                <Box>
                  <Typography variant="body2">{row.comment || row.reasonCode || "N/A"}</Typography>
                  {rulesCount > 0 && (
                    <Chip label={`${rulesCount} Rules Fired`} color="warning" size="small" sx={{ fontSize: "10px", height: "18px", mt: 0.5 }} />
                  )}
                </Box>
              );
            }
          },
          {
            field: "createdAt",
            headerName: "Decided Date",
            width: 180,
            renderCell: ({ value }) => formatDate(value)
          },
          {
            field: "actions",
            headerName: "Actions",
            width: 100,
            renderCell: ({ row }) => (
              <Button variant="outlined" color="inherit" size="small" onClick={() => handleInspect("decision", row)}>
                Details
              </Button>
            )
          }
        ];
      case 4: // Held Transactions
        return [
          { field: "reference", headerName: "Reference", width: 180, renderCell: ({ value }) => <strong>{value}</strong> },
          { field: "userId", headerName: "User ID", width: 150 },
          {
            field: "amount",
            headerName: "Amount",
            width: 150,
            renderCell: ({ row }) => {
              const currency = row.currency || "NGN";
              return row.amount ? `${currency} ${Number(row.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "N/A";
            }
          },
          {
            field: "status",
            headerName: "Status",
            width: 150,
            renderCell: ({ value }) => {
              let color = "warning";
              const val = String(value || "").toUpperCase();
              if (val === "APPROVED") color = "success";
              else if (val === "REJECTED") color = "error";
              else if (val === "PENDING_CHECKER") color = "info";

              return <Chip label={value} color={color} size="small" sx={{ fontWeight: 600 }} />;
            }
          },
          { field: "makerComment", headerName: "Maker Reason", width: 180 },
          {
            field: "createdAt",
            headerName: "Date Held",
            width: 180,
            renderCell: ({ value }) => formatDate(value)
          },
          {
            field: "actions",
            headerName: "Actions",
            width: 320,
            renderCell: ({ row }) => {
              const status = String(row.status || "").toUpperCase();
              const canMakerAction = isMaker && status === "HELD";
              const canCheckerAction = isChecker && (status === "PENDING_CHECKER" || status === "HELD");

              return (
                <Box display="flex" gap={1}>
                  <Button variant="outlined" color="inherit" size="small" onClick={() => handleInspect("held", row)}>
                    Inspect
                  </Button>
                  {canMakerAction && (
                    <>
                      <Button variant="contained" color="info" size="small" onClick={() => handleOpenActionDialog("HELD_MAKER_APPROVE", row, "Propose Approval", `Propose APPROVE for transaction ${row.reference}?`)}>
                        Prop Appr
                      </Button>
                      <Button variant="contained" color="error" size="small" onClick={() => handleOpenActionDialog("HELD_MAKER_REJECT", row, "Propose Rejection", `Propose REJECT for transaction ${row.reference}?`)}>
                        Prop Rej
                      </Button>
                    </>
                  )}
                  {canCheckerAction && (
                    <>
                      {status === "PENDING_CHECKER" && (
                        <Button variant="contained" color="success" size="small" onClick={() => handleOpenActionDialog("HELD_CHECKER_APPROVE", row, "Final Approve", `Confirm final APPROVE for transaction ${row.reference}?`)}>
                          Approve
                        </Button>
                      )}
                      <Button variant="contained" color="error" size="small" onClick={() => handleOpenActionDialog("HELD_CHECKER_REJECT", row, "Final Reject", `Confirm final REJECT for transaction ${row.reference}?`)}>
                        Reject
                      </Button>
                    </>
                  )}
                </Box>
              );
            }
          }
        ];
      default:
        return [];
    }
  };

  return (
    <Box m="20px">
      {/* Header */}
      <Header title="FRAUD MANAGEMENT" subtitle="Manage Fraud Rules & Action Decisions" />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, val) => setActiveTab(val)}
        textColor="secondary"
        indicatorColor="secondary"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        <Tab label="Fraud Rules" />
        <Tab label="Pending Decisions" />
        <Tab label="Counterparty Blocklist" />
        <Tab label="Decisions History" />
        <Tab label="Held Transactions" />
      </Tabs>

      {/* Sub-tabs for Fraud Rules / Events */}
      {activeTab === 0 && (
        <Box display="flex" gap={2} mb={3}>
          <Button
            variant={!isEvent ? "contained" : "outlined"}
            color="secondary"
            onClick={() => setIsEvent(false)}
          >
            Fraud Rules
          </Button>
          <Button
            variant={isEvent ? "contained" : "outlined"}
            color="secondary"
            onClick={() => setIsEvent(true)}
          >
            Fraud Events
          </Button>
        </Box>
      )}

      {/* Stats Summary Cards for Fraud Rules & Events */}
      {activeTab === 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid xs={12} sm={4}>
            <Box backgroundColor={colors.primary[400]} p={2} borderRadius="8px" display="flex" alignItems="center">
              <StatBox
                title={isEvent ? eventsStats.total : rulesList.length}
                subtitle={isEvent ? "All Fraud Events" : "All Fraud Rules"}
                icon={<PlaylistAddIcon sx={{ color: colors.orangeAccent[600], fontSize: "26px" }} />}
              />
            </Box>
          </Grid>
          <Grid xs={12} sm={4}>
            <Box backgroundColor={colors.primary[400]} p={2} borderRadius="8px" display="flex" alignItems="center">
              <StatBox
                title={isEvent ? eventsStats.resolved : rulesList.filter(e => e.activated).length}
                subtitle={isEvent ? "Resolved Fraud Events" : "Active Fraud Rules"}
                icon={<ExitToAppIcon sx={{ color: colors.orangeAccent[600], fontSize: "26px" }} />}
              />
            </Box>
          </Grid>
          <Grid xs={12} sm={4}>
            <Box backgroundColor={colors.primary[400]} p={2} borderRadius="8px" display="flex" alignItems="center">
              <StatBox
                title={isEvent ? eventsStats.unresolved : rulesList.filter(e => !e.activated).length}
                subtitle={isEvent ? "Unresolved Fraud Events" : "Inactive Fraud Rules"}
                icon={<AssignmentTurnedInIcon sx={{ color: colors.orangeAccent[600], fontSize: "26px" }} />}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Stats Summary Cards (Only on Decisions History tab) */}
      {activeTab === 3 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid xs={12} sm={4}>
            <Box backgroundColor={colors.primary[400]} p={2} borderRadius="8px" display="flex" alignItems="center">
              <StatBox
                title={decisionsStats.total}
                subtitle="Total Decisions"
                icon={<PlaylistAddIcon sx={{ color: colors.orangeAccent[600], fontSize: "26px" }} />}
              />
            </Box>
          </Grid>
          <Grid xs={12} sm={4}>
            <Box backgroundColor={colors.primary[400]} p={2} borderRadius="8px" display="flex" alignItems="center">
              <StatBox
                title={decisionsStats.approved}
                subtitle="Approved / Allowed"
                icon={<ExitToAppIcon sx={{ color: colors.orangeAccent[600], fontSize: "26px" }} />}
              />
            </Box>
          </Grid>
          <Grid xs={12} sm={4}>
            <Box backgroundColor={colors.primary[400]} p={2} borderRadius="8px" display="flex" alignItems="center">
              <StatBox
                title={decisionsStats.rejected}
                subtitle="Rejected / Blocked"
                icon={<AssignmentTurnedInIcon sx={{ color: colors.orangeAccent[600], fontSize: "26px" }} />}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Toolbar / Search Filtering Area */}
      <Box display="flex" justifyContent={activeTab === 0 ? "flex-end" : "space-between"} alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        {activeTab === 1 && (
          <TextField
            placeholder="Search pending decisions by reference or account..."
            size="small"
            value={searchPendingQuery}
            onChange={(e) => setSearchPendingQuery(e.target.value)}
            sx={{ width: 400 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "action.active", mr: 1 }} />
            }}
          />
        )}

        {activeTab === 2 && (
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              placeholder="Search blocklist..."
              size="small"
              value={searchBlockQuery}
              onChange={(e) => setSearchBlockQuery(e.target.value)}
              sx={{ width: 350 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "action.active", mr: 1 }} />
              }}
            />
            <Button variant="contained" color="secondary" onClick={() => setOpenAddBlocklistModal(true)}>
              Add to Blocklist
            </Button>
          </Box>
        )}

        {activeTab === 4 && (
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ width: 180 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={heldStatusFilter}
                label="Filter by Status"
                onChange={(e) => {
                  setHeldStatusFilter(e.target.value);
                  setHeldPage(1);
                }}
              >
                <MenuItem value="">ALL STATUSES</MenuItem>
                <MenuItem value="HELD">HELD</MenuItem>
                <MenuItem value="PENDING_CHECKER">PENDING CHECKER</MenuItem>
                <MenuItem value="APPROVED">APPROVED</MenuItem>
                <MenuItem value="REJECTED">REJECTED</MenuItem>
              </Select>
            </FormControl>

            <TextField
              placeholder="Search by User ID..."
              size="small"
              value={heldUserIdFilter}
              onChange={(e) => {
                setHeldUserIdFilter(e.target.value);
                setHeldPage(1);
              }}
              sx={{ width: 250 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "action.active", mr: 1 }} />
              }}
            />
          </Box>
        )}

        <Button
          variant="outlined"
          color="inherit"
          startIcon={<RefreshIcon />}
          onClick={() => {
            if (activeTab === 0) {
              if (isEvent) fetchFraudEvents();
              else fetchFraudRules();
            }
            else if (activeTab === 1) fetchPendingDecisions();
            else if (activeTab === 2) fetchBlocklist();
            else if (activeTab === 3) fetchDecisionsHistory();
            else if (activeTab === 4) fetchHeldTransactions();
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Main Table */}
      <Box
        height="65vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .name-column--cell": { color: colors.orangeAccent[300] },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.redAccent[700], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.redAccent[700] },
          "& .MuiCheckbox-root": { color: `${colors.orangeAccent[200]} !important` },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${colors.grey[100]} !important` }
        }}
      >
        <DataGrid
          rows={
            activeTab === 0
              ? (isEvent ? eventsList : rulesList)
              : activeTab === 1
              ? getFilteredPendingActions()
              : activeTab === 2
              ? getFilteredBlocklist()
              : activeTab === 3
              ? decisions
              : heldTransactions
          }
          columns={getColumns()}
          loading={loading}
          getRowId={(row) => row.id}
          pageSize={activeTab === 3 ? decisionsPageSize : activeTab === 4 ? heldPageSize : 100}
          paginationMode={activeTab === 3 || activeTab === 4 ? "server" : "client"}
          rowCount={activeTab === 3 ? decisionsTotalRows : activeTab === 4 ? heldTotalRows : undefined}
          page={activeTab === 3 ? decisionsPage - 1 : activeTab === 4 ? heldPage - 1 : undefined}
          onPageChange={(newPage) => {
            if (activeTab === 3) setDecisionsPage(newPage + 1);
            else if (activeTab === 4) setHeldPage(newPage + 1);
          }}
          onPageSizeChange={(newPageSize) => {
            if (activeTab === 3) {
              setDecisionsPageSize(newPageSize);
              setDecisionsPage(1);
            } else if (activeTab === 4) {
              setHeldPageSize(newPageSize);
              setHeldPage(1);
            }
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Box>

      {/* Bulk Add Blocklist Modal */}
      <Dialog open={openAddBlocklistModal} onClose={() => setOpenAddBlocklistModal(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleAddBlocklist}>
          <DialogTitle>Add Accounts to Blocklist</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField
                label="Account Number(s)"
                placeholder="Separate multiple accounts with commas (e.g. 1020304050, 6070809000)"
                multiline
                rows={4}
                value={blockAccountsInput}
                onChange={(e) => setBlockAccountsInput(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Reason for Blocklisting"
                placeholder="e.g. Suspicious transaction source"
                value={blockReasonInput}
                onChange={(e) => setBlockReasonInput(e.target.value)}
                required
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAddBlocklistModal(false)} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="secondary">
              Add Accounts
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Unified Action Dialog */}
      <Dialog
        open={openActionDialog}
        onClose={() => !actionLoading && setOpenActionDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <form
          onSubmit={(e) => {
            if (actionTypeStr === "BLOCKLIST_REMOVE") {
              e.preventDefault();
              handleRemoveBlocklistSubmit();
            } else {
              handleActionSubmit(e);
            }
          }}
        >
          <DialogTitle>{actionTitle}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" mb={2}>
              {actionSubtitle}
            </Typography>
            {actionTypeStr !== "BLOCKLIST_REMOVE" && (
              <TextField
                label="Comments / Reason (Required)"
                placeholder="Enter justification text..."
                multiline
                rows={3}
                value={actionComment}
                onChange={(e) => setActionComment(e.target.value)}
                required
                fullWidth
                autoFocus
                disabled={actionLoading}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenActionDialog(false)} color="inherit" disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color={actionTypeStr.includes("REJECT") || actionTypeStr.includes("REMOVE") ? "error" : "success"}
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={24} /> : "Confirm"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Detail Drawer (Inspector) */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: smScreen ? 600 : "100vw", p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h4" fontWeight="600">
              Inspection Details
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />

          {inspectDetailLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
              <CircularProgress color="secondary" />
            </Box>
          ) : inspectRecord ? (
            <Box mt={3} display="flex" flexDirection="column" gap={3}>
              {/* Context Section */}
              <Box>
                <Typography variant="h5" fontWeight="600" mb={1} color={colors.orangeAccent[400]}>
                  Core Info
                </Typography>
                <Grid container spacing={2}>
                  <Grid xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Reference / ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {inspectRecord.reference || inspectRecord.id || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      User ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {inspectRecord.userId || inspectRecord.checkerEmail || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Amount
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {inspectRecord.amount !== undefined ? `₦${inspectRecord.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "N/A"}
                    </Typography>
                  </Grid>
                  <Grid xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Status
                    </Typography>
                    <Box mt={0.5}>
                      <Chip label={inspectRecord.status || "PENDING"} size="small" color={inspectRecord.status === "APPROVED" ? "success" : inspectRecord.status === "REJECTED" ? "error" : "warning"} />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Identity & Origin */}
              <Box>
                <Typography variant="h5" fontWeight="600" mb={1} color={colors.orangeAccent[400]}>
                  Origin & Identity Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      From Account
                    </Typography>
                    <Typography variant="body2">{inspectRecord.fromAccount || "N/A"}</Typography>
                  </Grid>
                  <Grid xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      To Account
                    </Typography>
                    <Typography variant="body2">{inspectRecord.toAccount || "N/A"}</Typography>
                  </Grid>
                  <Grid xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Created / Held At
                    </Typography>
                    <Typography variant="body2">{formatDate(inspectRecord.createdAt)}</Typography>
                  </Grid>
                  <Grid xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Channel
                    </Typography>
                    <Typography variant="body2">{inspectRecord.channel || "N/A"}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Maker Checker Details */}
              <Box>
                <Typography variant="h5" fontWeight="600" mb={1} color={colors.orangeAccent[400]}>
                  Decision Context & Rules
                </Typography>
                <Grid container spacing={2}>
                  <Grid xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Comments / Reason
                    </Typography>
                    <Typography variant="body2">{inspectRecord.comment || inspectRecord.reason || inspectRecord.reasonCode || "N/A"}</Typography>
                  </Grid>
                  <Grid xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Maker / Initiated By
                    </Typography>
                    <Typography variant="body2">{inspectRecord.makerEmail || "N/A"}</Typography>
                  </Grid>
                  <Grid xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Checker / Approved By
                    </Typography>
                    <Typography variant="body2">{inspectRecord.checkerEmail || "N/A"}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Rules Fired JSON details */}
              {inspectRecord.rulesFiredJson && inspectRecord.rulesFiredJson !== "[]" && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="h5" fontWeight="600" mb={1} color={colors.orangeAccent[400]}>
                      Fired Fraud Rules
                    </Typography>
                    <Box
                      p={2}
                      borderRadius="4px"
                      sx={{
                        backgroundColor: theme.palette.mode === "dark" ? colors.primary[900] : colors.grey[900],
                        color: colors.grey[100],
                        fontFamily: "monospace",
                        fontSize: "11px",
                        overflowX: "auto"
                      }}
                    >
                      <pre style={{ margin: 0 }}>
                        {(() => {
                          try {
                            const parsed = JSON.parse(inspectRecord.rulesFiredJson);
                            return JSON.stringify(parsed, null, 2);
                          } catch (e) {
                            return String(inspectRecord.rulesFiredJson);
                          }
                        })()}
                      </pre>
                    </Box>
                  </Box>
                </>
              )}

              {/* Additional metadata */}
              {inspectRecord.metadata && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="h5" fontWeight="600" mb={1} color={colors.orangeAccent[400]}>
                      Transaction Metadata
                    </Typography>
                    <Box
                      p={2}
                      borderRadius="4px"
                      sx={{
                        backgroundColor: theme.palette.mode === "dark" ? colors.primary[900] : colors.grey[900],
                        color: colors.grey[100],
                        fontFamily: "monospace",
                        fontSize: "11px",
                        overflowX: "auto"
                      }}
                    >
                      <pre style={{ margin: 0 }}>
                        {typeof inspectRecord.metadata === "object"
                          ? JSON.stringify(inspectRecord.metadata, null, 2)
                          : String(inspectRecord.metadata)}
                      </pre>
                    </Box>
                  </Box>
                </>
              )}

              {/* Drawer actions at bottom */}
              {inspectType !== "decision" && (
                <Box mt={4} display="flex" gap={2}>
                  {inspectType === "pending" && (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        onClick={() => handleOpenActionDialog("PENDING_APPROVE", inspectRecord, "Approve Fraud Action", `Are you sure you want to approve this action for reference: ${inspectRecord.reference}?`)}
                      >
                        Approve Action
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        fullWidth
                        onClick={() => handleOpenActionDialog("PENDING_REJECT", inspectRecord, "Reject Fraud Action", `Are you sure you want to reject this action for reference: ${inspectRecord.reference}?`)}
                      >
                        Reject Action
                      </Button>
                    </>
                  )}

                  {inspectType === "held" && (
                    <>
                      {isMaker && String(inspectRecord.status).toUpperCase() === "HELD" && (
                        <>
                          <Button
                            variant="contained"
                            color="info"
                            fullWidth
                            onClick={() => handleOpenActionDialog("HELD_MAKER_APPROVE", inspectRecord, "Propose Approval", `Propose APPROVE for transaction ${inspectRecord.reference}?`)}
                          >
                            Propose Approve
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            fullWidth
                            onClick={() => handleOpenActionDialog("HELD_MAKER_REJECT", inspectRecord, "Propose Rejection", `Propose REJECT for transaction ${inspectRecord.reference}?`)}
                          >
                            Propose Reject
                          </Button>
                        </>
                      )}
                      {isChecker && (
                        <>
                          {String(inspectRecord.status).toUpperCase() === "PENDING_CHECKER" && (
                            <Button
                              variant="contained"
                              color="success"
                              fullWidth
                              onClick={() => handleOpenActionDialog("HELD_CHECKER_APPROVE", inspectRecord, "Final Approve", `Confirm final APPROVE for transaction ${inspectRecord.reference}?`)}
                            >
                              Approve Transaction
                            </Button>
                          )}
                          {(String(inspectRecord.status).toUpperCase() === "PENDING_CHECKER" || String(inspectRecord.status).toUpperCase() === "HELD") && (
                            <Button
                              variant="contained"
                              color="error"
                              fullWidth
                              onClick={() => handleOpenActionDialog("HELD_CHECKER_REJECT", inspectRecord, "Final Reject", `Confirm final REJECT for transaction ${inspectRecord.reference}?`)}
                            >
                              Reject Transaction
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Box mt={3}>
              <Typography>No record selected</Typography>
            </Box>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default ManageFraud;
